import { FC, useEffect, useRef, useState } from 'react'
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { BiographyModel } from '../models/BiographyModel'
import { updateUserBiography } from '../../../services/biographyService'
import { useHistory } from 'react-router-dom'
import { useUserProfileContext } from '../../../context/UserProfileContext'
import { useAlert } from '../../../hooks/utils/useAlert'
import { showErrorAlert } from '../../../helpers/alert'
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill'

const MAX_BIO_LENGTH = 10000
const MAX_URLS = 3

const BiographyForm: FC = () => {
  const [loading, setLoading] = useState(false)
  const quillRef = useRef<ReactQuill>(null)
  const navigate = useHistory()
  const { refetch, searchParams, data } = useUserProfileContext()
  const { showError } = useAlert()

  const validationSchema = Yup.object({
    biography: Yup.string().required('your biography is required'),
    urls: Yup.array()
      .of(
        Yup.string()
          .transform((value) => {
            if (!value) return value

            let normalizedUrl = String(value).trim()

            if (
              normalizedUrl &&
              !normalizedUrl.startsWith('http://') &&
              !normalizedUrl.startsWith('https://')
            ) {
              normalizedUrl = `https://${normalizedUrl}`
            }

            return normalizedUrl
          })
          .url('Must be a valid URL')
      )
      .max(MAX_URLS, `You can only add up to ${MAX_URLS} URLs`),
  })

  const [bioLength, setBioLength] = useState(0)

  // useEffect(() => {
  //   if (quillRef.current) {
  //     setBioLength(
  //       quillRef.current.getEditor().getLength() - 1
  //     )
  //   }
  // }, [data?.biography])

  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    // Si la biografía llega después del montaje, cargarla al editor (no controlado)
    if (data?.biography && editor.getLength() <= 1) {
      editor.clipboard.dangerouslyPasteHTML(data.biography)
    }

    setBioLength(editor.getLength() - 1)
  }, [data?.biography])

  const initialValues: BiographyModel = {
    biography: data?.biography ?? '',
    urls: data?.videos && data.videos.length > 0
      ? data.videos.map(video => video.videoLink)
      : [''],
  }

  const handleSubmit = async (values: BiographyModel) => {
    try {
      setLoading(true)
      const normalizedUrls = values.urls
        .filter((url) => url.trim() !== '')
        .map((url) => {
          let normalizedUrl = String(url ?? '').trim()

          if (
            normalizedUrl &&
            !normalizedUrl.startsWith('http://') &&
            !normalizedUrl.startsWith('https://')
          ) {
            normalizedUrl = `https://${normalizedUrl}`
          }

          return normalizedUrl
        })

      const payload = {
        biography: values.biography,
        urls: normalizedUrls,
      }

      let isSaved = await updateUserBiography(payload, (!!searchParams.userSelected ? searchParams.userSelected : ''));
      if (isSaved?.biography) {
        await refetch()
        showErrorAlert({ title: 'Saved', message: 'Your information has been saved successfully.' });
        navigate.goBack()
      }
    } catch (error: any) {
      const statusCode = error?.response?.status || 500
      showError({
        title: 'Unable to update biography',
        message: "We couldn't save your changes. Please review your information and try again.",
        errorCode: `BIOGRAPHY_${statusCode}`,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='biography-form-container'>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form className='biography-form'>
            {/* Title */}
            <h1 className='biography-title'>Edit Your Biography</h1>

            {/* Biography */}
            <div className='biography-section'>
              <label className='section-label'>Your Biography</label>
              <p className='section-description'>
                Tell us about your journey—what shaped you, what drives you, and what you aspire to
                achieve. Please write a brief biography including your professional background, key
                accomplishments, and personal interests. Focus on the defining moments, decisions,
                and experiences that have shaped your path.
              </p>
              <ReactQuill theme="snow" className='ql-editor-biography'
                ref={quillRef}
                defaultValue={initialValues.biography}
                onChange={(content, delta, source, editor) => {
                  const textLength = editor.getLength() - 1

                  if (textLength > MAX_BIO_LENGTH) {
                    const quill = quillRef.current?.getEditor()

                    quill?.deleteText(
                      MAX_BIO_LENGTH,
                      textLength - MAX_BIO_LENGTH
                    )

                    return
                  }

                  setBioLength(textLength)
                  setFieldValue('biography', content)
                }}
              />
              <div className='textarea-wrapper m-4'>
                {/* <Field
                  as='textarea'
                  name='biography'
                  className='textarea-input'
                  maxLength={MAX_BIO_LENGTH}
                  onChange={handleChange}
                /> */}
                <div className='textarea-footer'>
                  <span className='textarea-hint'>Share your biography. Maximum {MAX_BIO_LENGTH}</span>
                  <span className='textarea-counter'>
                    {bioLength}/{MAX_BIO_LENGTH}
                  </span>
                </div>
                <ErrorMessage name="biography" component="div" className="tap-add-experience-text text-danger" />
              </div>
            </div>

            {/* YouTube Videos */}
            <div className='biography-section'>
              <label className='section-label'>YouTube Videos</label>
              <p className='section-description'>
                Upload up to three videos to showcase your story.
              </p>

              <FieldArray name='urls'>
                {({ push, remove }) => (
                  <div className='youtube-url-group'>
                    {values.urls.map((_, index) => (
                      <div key={index} className='youtube-input-wrapper'>
                        <label className='youtube-input-label'>YouTube URL</label>
                        <div className='youtube-input-container'>
                          <Field name={`urls[${index}]`} className='youtube-input' />
                        </div>
                        <ErrorMessage name={`urls[${index}]`} component="div" className="tap-add-experience-text text-danger" />
                      </div>
                    ))}

                    {values.urls.length < MAX_URLS && (
                      <button type='button' className='add-more-btn' onClick={() => push('')}>
                        + Add more
                      </button>
                    )}
                  </div>
                )}
              </FieldArray>
            </div>

            {/* Actions */}
            <div className='form-actions'>
              <button type='button' className='cancel-btn' onClick={() => navigate.goBack()}>
                Cancel
              </button>
              <button
                type='submit'
                className='btn nb-btn-primary'
                disabled={loading}
                aria-busy={loading ? 'true' : 'false'}
                aria-live='polite'
              >
                {!loading ? (
                  <>
                    <span className='nb-heading-md'>save changes</span>
                    <img
                      src='/media/svg/nobilis/vector1.svg'
                      alt=''
                      className='nb-btn-icon nb-btn-icon--white'
                    />
                  </>
                ) : (
                  <span className='indicator-progress nb-heading-md'>
                    Please wait...
                    <span
                      className='spinner-border spinner-border-sm align-middle ms-2'
                      role='status'
                      aria-hidden='true'
                    />
                  </span>
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default BiographyForm
